import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link, useOutletContext } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Alert,
  AlertTitle,
  CircularProgress,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  useTheme,
  Tooltip
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Today as TodayIcon,
  Add as AddIcon,
  MoreTime as MoreTimeIcon
} from '@mui/icons-material';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  isSameDay, 
  isToday, 
  isSameMonth,
  startOfWeek,
  endOfWeek,
  addWeeks,
  subWeeks,
  startOfDay,
  endOfDay,
  addDays,
  subDays,
  parseISO
} from 'date-fns';

import useTaskStore from '../../stores/taskStore';
import { alpha } from '@mui/material';

// Calculate calendar for the current month
const getCalendarDays = (date) => {
  const monthStart = startOfMonth(date);
  const monthEnd = endOfMonth(date);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  const startDay = monthStart.getDay();
  const endDay = 6 - monthEnd.getDay();
  
  const prevMonthDays = [];
  if (startDay > 0) {
    const prevMonthEnd = new Date(monthStart);
    prevMonthEnd.setDate(0);
    for (let i = startDay - 1; i >= 0; i--) {
      const day = new Date(prevMonthEnd);
      day.setDate(prevMonthEnd.getDate() - i);
      prevMonthDays.push(day);
    }
  }
  
  const nextMonthDays = [];
  if (endDay > 0) {
    const nextMonthStart = new Date(monthEnd);
    nextMonthStart.setDate(nextMonthStart.getDate() + 1);
    for (let i = 0; i < endDay; i++) {
      const day = new Date(nextMonthStart);
      day.setDate(nextMonthStart.getDate() + i);
      nextMonthDays.push(day);
    }
  }
  
  return [...prevMonthDays, ...daysInMonth, ...nextMonthDays];
};

// Get calendar days for week view
const getWeekDays = (date) => {
  const weekStart = startOfWeek(date);
  const weekEnd = endOfWeek(date);
  return eachDayOfInterval({ start: weekStart, end: weekEnd });
};

// Get calendar days for day view
const getDayOnly = (date) => {
  return [date];
};

// Get tasks for a specific date
const getTasksForDate = (date, tasks) => {
  return tasks.filter(task => {
    const dueDate = task.dueDate ? new Date(task.dueDate) : null;
    return dueDate && isSameDay(dueDate, date);
  });
};

// Get milestones for a specific date
const getMilestonesForDate = (date, milestones) => {
  return milestones.filter(milestone => {
    const dueDate = milestone.dueDate ? new Date(milestone.dueDate) : null;
    return dueDate && isSameDay(dueDate, date);
  });
};

const ProjectCalendar = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { project, canEdit } = useOutletContext();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarDays, setCalendarDays] = useState([]);
  const [viewType, setViewType] = useState('month');
  
  const { 
    calendarTasks,
    fetchCalendarTasks,
    isLoading: isLoadingTasks,
    error: tasksError,
    openTaskDrawer,
    loadMoreTasks,
    sectionPagination,
  } = useTaskStore();
  
  // Calculate calendar days when current date or view type changes
  useEffect(() => {
    switch (viewType) {
      case 'month':
        setCalendarDays(getCalendarDays(currentDate));
        break;
      case 'week':
        setCalendarDays(getWeekDays(currentDate));
        break;
      case 'day':
        setCalendarDays(getDayOnly(currentDate));
        break;
      default:
        setCalendarDays(getCalendarDays(currentDate));
    }
  }, [currentDate, viewType]);
  
  // Load tasks for the calendar view
  useEffect(() => {
    if (project?._id) {
      let startDate, endDate;
      
      switch (viewType) {
        case 'month':
          startDate = startOfMonth(currentDate);
          endDate = endOfMonth(currentDate);
          break;
        case 'week':
          startDate = startOfWeek(currentDate);
          endDate = endOfWeek(currentDate);
          break;
        case 'day':
          startDate = startOfDay(currentDate);
          endDate = endOfDay(currentDate);
          break;
        default:
          startDate = startOfMonth(currentDate);
          endDate = endOfMonth(currentDate);
      }
      
      fetchCalendarTasks(project._id, {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
    }
  }, [project, currentDate, viewType, fetchCalendarTasks]);
  
  // Navigation functions
  const goToPrevious = () => {
    switch (viewType) {
      case 'month':
        setCurrentDate(subMonths(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(subWeeks(currentDate, 1));
        break;
      case 'day':
        setCurrentDate(subDays(currentDate, 1));
        break;
    }
  };
  
  const goToNext = () => {
    switch (viewType) {
      case 'month':
        setCurrentDate(addMonths(currentDate, 1));
        break;
      case 'week':
        setCurrentDate(addWeeks(currentDate, 1));
        break;
      case 'day':
        setCurrentDate(addDays(currentDate, 1));
        break;
    }
  };
  
  const goToToday = () => {
    setCurrentDate(new Date());
  };
  
  const handleTaskClick = (task) => {
    openTaskDrawer(task);
  };

  // Handler for when a day is clicked - open the task drawer with the day pre-filled
  const handleDayClick = useCallback((day) => {
    if (canEdit) {
      // Create task context with the day as the due date
      const taskContext = {
        projectId: project?._id,
        dueDate: format(day, 'yyyy-MM-dd'),
        status: 'to_do'
      };
      
      // Open task drawer with this context
      openTaskDrawer(taskContext);
    }
  }, [canEdit, project, openTaskDrawer]);

  // Handler to load more tasks - loads for all sections
  const handleLoadMore = useCallback(() => {
    if (project?._id && sectionPagination[project._id]) {
      // Get all sections that have more tasks to load
      const projectPagination = sectionPagination[project._id];
      
      // Load more tasks for each section that has more to load
      Object.entries(projectPagination).forEach(([sectionId, sectionData]) => {
        if (sectionData && sectionData.hasMore) {
          // Convert string 'null' to actual null if needed
          const actualSectionId = sectionId === "null" ? null : sectionId;
          loadMoreTasks(project._id, actualSectionId);
        }
      });
    }
  }, [project, sectionPagination, loadMoreTasks]);

  // Function to check if there are more tasks to load across all sections
  const hasMoreTasks = useCallback(() => {
    if (!project?._id || !sectionPagination[project._id]) {
      return false;
    }
    
    // Check if ANY section has more content to load (including null section)
    const projectPagination = sectionPagination[project._id];
    return Object.values(projectPagination).some(sectionData => 
      sectionData && sectionData.hasMore
    );
  }, [project, sectionPagination]);

  if (tasksError) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        <AlertTitle>Error</AlertTitle>
        {tasksError}
      </Alert>
    );
  }
  
  if (!project) {
    return (
      <Alert severity="warning">
        <AlertTitle>Project not found</AlertTitle>
        The project you're looking for doesn't exist or you don't have access to it.
        <Box mt={2}>
          <Link to="/projects">Return to Projects</Link>
        </Box>
      </Alert>
    );
  }
  
  // Get date range for current view
  let startDate, endDate;
  switch (viewType) {
    case 'month':
      startDate = startOfMonth(currentDate);
      endDate = endOfMonth(currentDate);
      break;
    case 'week':
      startDate = startOfWeek(currentDate);
      endDate = endOfWeek(currentDate);
      break;
    case 'day':
      startDate = startOfDay(currentDate);
      endDate = endOfDay(currentDate);
      break;
    default:
      startDate = startOfMonth(currentDate);
      endDate = endOfMonth(currentDate);
  }
  
  const cacheKey = `${project._id}-${startDate.toISOString()}-${endDate.toISOString()}`;
  const tasks = calendarTasks[cacheKey] || [];
  const milestones = project.milestones || [];
  
  // Format the display based on view type
  const getDisplayFormat = () => {
    switch (viewType) {
      case 'month':
        return format(currentDate, 'MMMM yyyy');
      case 'week':
        return `${format(startOfWeek(currentDate), 'MMM d')} - ${format(endOfWeek(currentDate), 'MMM d, yyyy')}`;
      case 'day':
        return format(currentDate, 'EEEE, MMMM d, yyyy');
      default:
        return format(currentDate, 'MMMM yyyy');
    }
  };
  
  // Determine grid columns based on view type
  const getGridColumns = () => {
    switch (viewType) {
      case 'month':
        return 'repeat(7, 1fr)';
      case 'week':
        return 'repeat(7, 1fr)';
      case 'day':
        return '1fr';
      default:
        return 'repeat(7, 1fr)';
    }
  };
  
  return (
    <Box>
      {/* Calendar Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={goToPrevious}>
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h6">
              {getDisplayFormat()}
            </Typography>
            <IconButton onClick={goToNext}>
              <ChevronRightIcon />
            </IconButton>
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={<TodayIcon />}
              onClick={goToToday}
              sx={{ ml: 1 }}
            >
              Today
            </Button>
          </Box>
          
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel id="view-type-label">View</InputLabel>
            <Select
              labelId="view-type-label"
              id="view-type"
              value={viewType}
              label="View"
              onChange={(e) => setViewType(e.target.value)}
            >
              <MenuItem value="month">Month</MenuItem>
              <MenuItem value="week">Week</MenuItem>
              <MenuItem value="day">Day</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>
      
      {/* Calendar */}
      <Paper sx={{ p: 2 }}>
        {/* Weekday Headers - only for month and week views */}
        {viewType !== 'day' && (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 1 }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
              <Box key={index} sx={{ textAlign: 'center', p: 1 }}>
                <Typography variant="subtitle2" fontWeight="medium">
                  {day}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
        
        {/* Calendar Grid */}
        <Box sx={{ 
          display: 'grid', 
          gridTemplateColumns: getGridColumns(),
          gridAutoRows: viewType === 'day' ? 'auto' : 'minmax(120px, auto)',
          gap: 1,
          bgcolor: theme.palette.background.default
        }}>
          {calendarDays.map((day, index) => {
            const tasksForDay = getTasksForDate(day, tasks);
            const milestonesForDay = getMilestonesForDate(day, milestones);
            const isCurrentMonth = viewType === 'month' ? isSameMonth(day, currentDate) : true;
            
            // Format day to use as identifier
            const dayStr = format(day, 'yyyy-MM-dd');
            
            return (
              <Box 
                key={index} 
                sx={{ 
                  p: 1.5,
                  bgcolor: isCurrentMonth 
                    ? theme.palette.background.paper
                    : theme.palette.mode === 'dark'
                      ? alpha(theme.palette.background.paper, 0.7)
                      : alpha(theme.palette.background.default, 0.8),
                  border: isToday(day) 
                    ? theme.palette.mode === 'dark'
                      ? `2px solid ${theme.palette.secondary.main}`
                      : `2px solid ${theme.palette.primary.main}`
                    : isCurrentMonth
                      ? `1px solid ${theme.palette.divider}`
                      : theme.palette.mode === 'dark'
                        ? `1px solid ${alpha(theme.palette.divider, 0.3)}`
                        : `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                  borderRadius: 1,
                  opacity: isCurrentMonth ? 1 : (theme.palette.mode === 'dark' ? 0.7 : 0.6),
                  minHeight: viewType === 'day' ? '200px' : '120px',
                  overflow: 'hidden',
                  cursor: canEdit ? 'pointer' : 'default',
                  transition: 'all 0.2s',
                  boxShadow: isToday(day) 
                    ? theme.palette.mode === 'dark'
                      ? `0 0 8px ${alpha(theme.palette.secondary.main, 0.4)}`
                      : `0 0 4px ${alpha(theme.palette.primary.main, 0.3)}`
                    : 'none',
                  '&:hover': canEdit ? {
                    borderColor: theme.palette.mode === 'dark' 
                      ? theme.palette.secondary.main 
                      : theme.palette.primary.main,
                    boxShadow: theme.palette.mode === 'dark'
                      ? `0 2px 8px ${alpha(theme.palette.secondary.main, 0.3)}`
                      : 1,
                    transform: 'translateY(-2px)',
                    bgcolor: isToday(day) 
                      ? theme.palette.mode === 'dark' 
                        ? alpha(theme.palette.secondary.main, 0.25)
                        : alpha(theme.palette.primary.light, 0.2)
                      : theme.palette.mode === 'dark'
                        ? alpha(theme.palette.background.paper, 0.9)
                        : theme.palette.background.paper
                  } : {}
                }}
                onClick={() => handleDayClick(day)}
                data-date={dayStr}
              >
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 1
                }}>
                  <Typography 
                    variant={viewType === 'day' ? 'h6' : 'body2'} 
                    fontWeight={isToday(day) ? 'bold' : isCurrentMonth ? 'medium' : 'normal'}
                    color={isToday(day) 
                      ? theme.palette.mode === 'dark' 
                        ? theme.palette.secondary.main 
                        : theme.palette.primary.main
                      : isCurrentMonth 
                        ? 'textPrimary' 
                        : theme.palette.mode === 'dark'
                          ? alpha(theme.palette.text.primary, 0.8)
                          : alpha(theme.palette.text.primary, 0.6)}
                    sx={{
                      textShadow: isToday(day) && theme.palette.mode === 'dark' 
                        ? `0 0 4px ${alpha(theme.palette.secondary.main, 0.5)}`
                        : 'none'
                    }}
                  >
                    {viewType === 'day' ? format(day, 'EEEE, MMMM d, yyyy') : format(day, 'd')}
                  </Typography>
                  
                  {canEdit && (
                    <Tooltip title="Add task for this day">
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDayClick(day);
                        }}
                        sx={{ 
                          opacity: 0.7,
                          '&:hover': { opacity: 1 }
                        }}
                      >
                        <AddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                
                {/* Milestones for the day */}
                {milestonesForDay.map(milestone => (
                  <Chip
                    key={milestone._id}
                    label={milestone.name}
                    size="small"
                    color="secondary"
                    sx={{ mb: 0.5, width: '100%', overflow: 'hidden' }}
                  />
                ))}
                
                {/* Tasks for the day */}
                <Box sx={{ maxHeight: viewType === 'day' ? '300px' : '150px', overflowY: 'auto' }}>
                  {tasksForDay.map(task => (
                    <Box 
                      key={task._id}
                      sx={{
                        bgcolor: (() => {
                          switch(task.status) {
                            case 'done': return alpha(theme.palette.success.main, 0.1);
                            case 'in_progress': return alpha(theme.palette.info.main, 0.1);
                            case 'in_review': return alpha(theme.palette.warning.main, 0.1);
                            default: return alpha(theme.palette.grey[500], 0.1);
                          }
                        })(),
                        p: 0.5,
                        borderRadius: 1,
                        mb: 0.5,
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.1)
                        }
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleTaskClick(task);
                      }}
                    >
                      <Typography variant="caption" noWrap sx={{ fontWeight: 'medium' }}>
                        {task.title}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                
                {/* Load More Button - shown at the bottom of any day cell when more tasks can be loaded */}
                {hasMoreTasks() && (
                  <Button
                    size="small"
                    startIcon={<MoreTimeIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLoadMore();
                    }}
                    sx={{ 
                      mt: 1,
                      fontSize: '0.7rem',
                      textTransform: 'none',
                      borderRadius: theme.shape.borderRadius * 2,
                      p: '2px 8px'
                    }}
                  >
                    Load more tasks
                  </Button>
                )}
              </Box>
            );
          })}
        </Box>
        
        {/* Legend */}
        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${theme.palette.divider}`, display: 'flex', gap: 2 }}>
          <Chip size="small" color="secondary" label="Milestone" />
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: alpha(theme.palette.success.main, 0.1), borderRadius: 1, mr: 0.5 }} />
            <Typography variant="caption">Done</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 1, mr: 0.5 }} />
            <Typography variant="caption">In Progress</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 1, mr: 0.5 }} />
            <Typography variant="caption">In Review</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box sx={{ width: 16, height: 16, bgcolor: alpha(theme.palette.grey[500], 0.1), borderRadius: 1, mr: 0.5 }} />
            <Typography variant="caption">To Do</Typography>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProjectCalendar;